/*  MUI  */
import { CircularProgress } from '@mui/material';
import { setTenant, setTenantError } from 'actions';

/*  Shared  */
import { Helpers, Response, SessionHelper } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';

/*  React  */
import axios from 'axios';
import { FEATURE_FLAGS_FROM_TENANT_SETTINGS } from 'featureFlags/src';
import { useSession } from 'hooks/redux/Selectors/session';
import { useTenantInfoByTenantId } from 'hooks/redux/Selectors/tenants';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import { PermissionsErrorPage } from './PermissionsErrorPage';

const loadClientSettings = async (tenantId, settingKeys) => {
    const settings = {};
    const params = {
        includeClients: true,
        platformTenantId: tenantId
    };

    for (let i = 0; i < settingKeys.length; i += 1) {
        const key = settingKeys[i];

        const { CancelToken } = axios;
        const source = CancelToken.source();
        try {
            // eslint-disable-next-line no-await-in-loop
            const response = await axios.get(`/tenantSetting/${key}`, {
                cancelToken: source.token,
                params
            });

            if (Helpers.isset(response, 'data', 'data', 0, 'value')) {
                settings[key] = response.data.data[0].value;
            } else {
                debug.w('Failed to get Setting', response);
            }
        } catch (e) {
            debug.w('Error getting Settings axios call', e);
        }
    }

    return settings;
};

const useTenantLoader = ({
    dispatchSuccessFn,
    dispatchErrorFn,
    tenantAlreadyLoaded,
    tenantId,
    isStale
}) => {
    const dispatch = useDispatch();
    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();
        async function fetchData() {
            const params = {
                id: tenantId,
                includeClients: true,
                includeSettings: true
            };

            try {
                const response = await axios.get('/tenant', {
                    cancelToken: source.token,
                    params
                });

                if (!Response.hasData(response)) {
                    debug.e('No Tenant Found');
                    dispatch(dispatchErrorFn(tenantId));
                    return;
                }

                const tenant = Response.getOne(response);

                if (!tenant.partner) {
                    // load some additional feature flag data.
                    const settings = await loadClientSettings(
                        tenant.id,
                        FEATURE_FLAGS_FROM_TENANT_SETTINGS
                    );

                    tenant.settings = {
                        ...tenant.settings,
                        ...settings
                    };
                }

                dispatch(dispatchSuccessFn(tenant.id, tenant));
            } catch (e) {
                debug.e('Error loading Tenant', e.toString());
                dispatch(dispatchErrorFn(tenantId));
            }
        }

        const shouldFetch =
            tenantId && (!tenantAlreadyLoaded || isStale === true);

        if (shouldFetch) {
            fetchData();
        }
        return () => {
            source.cancel();
        };
    }, [
        dispatch,
        dispatchSuccessFn,
        dispatchErrorFn,
        tenantAlreadyLoaded,
        tenantId,
        isStale
    ]);
};

/**
 * TenantLoader ensures that everytime the clientId and partnerId URL params change,
 * the tenant is fetched from the API and stored in redux.
 *
 * Then, where needed, we can fetch the tenants from redux throughout the app.
 */
export const TenantLoader = ({ children }) => {
    // On every route change, check if there is a partnerId OR clientId in the URL
    const { clientId, partnerId } = useParams();

    const session = useSession();
    const sessionHelper = new SessionHelper(session);

    // If there is, check if it exists in app state
    const partnerTenantInfo = useTenantInfoByTenantId(partnerId);
    const partnerTenant = partnerTenantInfo?.tenant;
    const partnerTenantError = partnerTenantInfo?.error;
    const partnerTenantIsStale = partnerTenantInfo?.isStale;

    const clientTenantInfo = useTenantInfoByTenantId(clientId);
    const clientTenant = clientTenantInfo?.tenant;
    const clientTenantError = clientTenantInfo?.error;
    const clientTenantIsStale = clientTenantInfo?.isStale;

    // Pass the tenant info the tenant loader.
    // (it may not load anything if the tenant was already loaded OR an error occurred while loading the tenant)
    // Partner loader
    useTenantLoader({
        dispatchErrorFn: setTenantError,
        dispatchSuccessFn: setTenant,
        isStale: partnerTenantIsStale,
        tenantAlreadyLoaded: !!partnerTenant || partnerTenantError,
        tenantId: partnerId
    });

    // Client loader
    useTenantLoader({
        dispatchErrorFn: setTenantError,
        dispatchSuccessFn: setTenant,
        isStale: clientTenantIsStale,
        tenantAlreadyLoaded: !!clientTenant || clientTenantError,
        tenantId: clientId
    });

    // If an error loading a tenant occurs, show the PermissionsError page.
    if (partnerTenantError || clientTenantError) {
        return <PermissionsErrorPage />;
    }

    // If a partner/client Id is found, but not partner/client data, show a loading spinner.
    if ((partnerId && !partnerTenant) || (clientId && !clientTenant)) {
        return (
            <div className="App" id="App">
                <span
                    className="no-print"
                    data-testid="TenantLoaderSpinner"
                    style={{
                        display: 'block',
                        left: '50%',
                        margin: 'auto',
                        position: 'absolute',
                        textAlign: 'center',
                        top: ' 25%'
                    }}
                >
                    <CircularProgress />
                </span>
            </div>
        );
    }

    // If the current session is global admin and the loaded tenant has opted out of Auvik Support Access,
    // show the PermissionsError page.
    if (
        sessionHelper.isGlobalAdmin() &&
        partnerTenant &&
        // make sure the global admin isn't viewing itself
        partnerTenant.id !== sessionHelper.getTenantId()
    ) {
        // If the tenant doesn't have the 'enableAuvikSupportAccess' setting, default to true
        const enableAuvikSupportAccess = Helpers.isset(
            partnerTenant,
            'settings',
            'enableAuvikSupportAccess'
        )
            ? partnerTenant.settings.enableAuvikSupportAccess
            : true;
        if (!enableAuvikSupportAccess) {
            return <PermissionsErrorPage />;
        }
    }

    // Once the view partner/client tenants are loaded, render the children.
    return children;
};
